import { getInsightsChannel } from "@hightouch/lib/ad-studio/insights-channels/registry";
import type {
  AdLayouts,
  AggType,
  CubeMember,
  CubeMeta,
  CubeSegment,
  AdView,
  CubeView,
  Grain,
  FeaturedGroupBy,
  GroupByTarget,
} from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-meta";
import { cubeMetaSchema } from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-meta";
import { cubeQuerySchema } from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-query";
import {
  cubeBlendResponseSchema,
  cubeLoadResponseSchema,
} from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-result";
import { runnableFilterSchema } from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-filter";
import type { AdInsightsCubeMetaQuery } from "src/graphql/types";
import { fatigueBadgeVariant } from "src/pages/ad-studio/insights/shared/fatigue";
import type {
  ChannelScope,
  CubeWire,
  Description,
  Folder,
  Found,
  AttributeGroupBy,
  GroupBy,
  FeaturedGroupTarget,
  GroupByChoice,
  GroupByLayout,
  GroupDimension,
  GroupTarget,
  GroupByKey,
  GroupByMenu,
  InsightsMeta,
  InsightsMetaResult,
  InsightsSync,
  Member,
  MetricColumn,
  MetricKey,
  NonEmptyArray,
  Segment,
  AdViewMeta,
  ViewMeta,
} from "src/pages/ad-studio/insights/performance/types";
import type {
  BadgeVariant,
  CellBadge,
} from "src/pages/ad-studio/insights/performance/view/types";
import { toEntityLabel } from "src/pages/ad-studio/insights/performance/utils/channels";

const toDescription = (description: string | undefined): Description =>
  description === undefined
    ? { kind: "no-description" }
    : { kind: "shown", text: description };

// cubeMetaSchema admits only a folder member a filter or column can use.
const toFilterMember = (member: CubeMember): Member[] => {
  const named = {
    name: member.name,
    shortTitle: member.shortTitle,
    operators: member.meta.operators,
  };
  switch (member.meta.kind) {
    case "measure": {
      return [
        {
          ...named,
          kind: "measure",
          format: member.meta.format,
          polarity: member.meta.polarity,
          channels: member.meta.channels,
          description: toDescription(member.description),
        },
      ];
    }
    case "value-list":
    case "creative-media":
    case "nested":
    case "number": {
      return [];
    }
    case "tag": {
      return [{ ...named, kind: "creative-tag" }];
    }
    case "listed": {
      return [{ ...named, kind: "listed", values: member.meta.values }];
    }
    case "queried":
    case "free-text":
    case "time":
    case "video-length": {
      return [{ ...named, kind: member.meta.kind }];
    }
    default: {
      return member.meta satisfies never;
    }
  }
};

// Records are parsed from the same cube as the labels, so a record's member always has one.
export const memberLabel = (view: ViewMeta, name: string): string =>
  view.labels.get(name) ?? name;

export const toMetaResult = (
  response: AdInsightsCubeMetaQuery,
): InsightsMetaResult => {
  const parsed = cubeMetaSchema.safeParse(response.adInsightsCubeMeta);
  return parsed.success
    ? { status: "loaded", value: toInsightsMeta(parsed.data) }
    : {
        status: "failed",
        failure: { cause: "bad-response", issues: parsed.error.issues },
      };
};

export const metricMember = (view: ViewMeta, key: MetricKey): string => {
  const column = view.metrics.find((metric) => metric.key === key);
  if (column === undefined) {
    throw new Error(`${key} is not a metric of ${view.name}`);
  }
  return column.member;
};

export const defaultSortOf = (view: ViewMeta): MetricKey =>
  metricOf(view, view.controls.sort.default).key;

export const fatigueMetricsOf = (view: AdViewMeta): readonly MetricColumn[] => {
  const { ctr, frequency, spend } = view.layouts.fatigue;
  return [ctr, frequency, spend].map((member) => metricOf(view, member));
};

export const drillTileMetricsOf = (view: ViewMeta): readonly MetricColumn[] =>
  view.metrics.filter((column) => column.place.includes("drill-tile"));

export const initialSegmentsOf = (view: ViewMeta): readonly string[] =>
  view.segments.flatMap((segment) =>
    segment.initially === "on" ? [segment.name] : [],
  );

// A summed or counted total grows with the rows behind it, so its average is per row; any other aggregate already is one.
const averageOf = (aggType: AggType): MetricColumn["average"] => {
  switch (aggType) {
    case "sum":
    case "count":
    case "countDistinct":
    case "countDistinctApprox": {
      return "per-row";
    }
    case "number":
    case "numberAgg":
    case "avg":
    case "min":
    case "max":
    case "rank":
    case "string":
    case "boolean":
    case "time": {
      return "as-reported";
    }
    default: {
      return aggType satisfies never;
    }
  }
};

const toMetricColumns = (cube: CubeView): readonly MetricColumn[] =>
  cube.measures.flatMap((measure): MetricColumn[] =>
    measure.meta.kind === "measure" && measure.meta.place.includes("metric")
      ? [
          {
            key: measure.aliasMember,
            member: measure.name,
            label: measure.shortTitle,
            format: measure.meta.format,
            polarity: measure.meta.polarity,
            channels: measure.meta.channels,
            place: measure.meta.place,
            whenEmpty: measure.meta.whenEmpty,
            description: toDescription(measure.description),
            average: averageOf(measure.aggType),
          },
        ]
      : [],
  );

// cubeMetaSchema checked that every metric a record names is placed as a metric, so a miss here is a bug.
export const metricOf = (view: ViewMeta, member: string): MetricColumn => {
  const column = view.metrics.find((column) => column.member === member);
  if (column === undefined) {
    throw new Error(`${member} is not a metric of ${view.name}`);
  }
  return column;
};

const toSegment = (segment: CubeSegment): Segment => ({
  name: segment.name,
  label: segment.shortTitle,
  initially: segment.meta.initially,
});

const membersOf = (cube: CubeView): ReadonlyMap<string, CubeMember> =>
  new Map(
    [...cube.measures, ...cube.dimensions].map(
      (member): [string, CubeMember] => [member.name, member],
    ),
  );

const toValueLabels = (
  cube: CubeView,
): ReadonlyMap<string, ReadonlyMap<string, string>> =>
  new Map(
    cube.dimensions.flatMap(({ name, meta }) =>
      meta.kind === "listed"
        ? [
            [
              name,
              new Map(
                meta.values.map(({ value, label }): [string, string] => [
                  value,
                  label,
                ]),
              ),
            ] as const,
          ]
        : [],
    ),
  );

const toGroupByLayout = ({
  groupBy,
  dimensions,
}: {
  groupBy: AdLayouts["groupBy"];
  dimensions: AdView["dimensions"];
}): GroupByLayout => {
  const byName = new Map(
    dimensions.map((dimension) => [dimension.name, dimension]),
  );
  const named = (member: string): GroupDimension => {
    const dimension = byName.get(member);
    const [groupDimension] =
      dimension === undefined ? [] : toFilterMember(dimension);
    // Unreachable: cubeMetaSchema admits only a group-dimension of the ad view in the Group by list.
    if (
      groupDimension === undefined ||
      groupDimension.kind === "measure" ||
      groupDimension.kind === "video-length"
    ) {
      throw new Error(`${member} is not a dimension Group by can use`);
    }
    return groupDimension;
  };
  const tags = dimensions
    .flatMap(toFilterMember)
    .flatMap((member) => (member.kind === "creative-tag" ? [member] : []));
  const toTarget = (target: GroupByTarget): GroupTarget => {
    switch (target.kind) {
      case "attribute": {
        return {
          kind: "attribute",
          dimension: named(target.member),
          segments: target.segments,
        };
      }
      case "none":
      case "entities": {
        return target;
      }
      case "creative-tags": {
        return { kind: "creative-tags", dimensions: tags };
      }
      default: {
        return target satisfies never;
      }
    }
  };
  const toFeatured = (entry: FeaturedGroupBy): FeaturedGroupTarget => {
    switch (entry.kind) {
      case "attribute": {
        return {
          kind: "attribute",
          dimension: named(entry.member),
          segments: entry.segments,
          label: entry.label,
          subtitle: entry.subtitle,
        };
      }
      case "none":
      case "entities": {
        return entry;
      }
      case "creative-tags": {
        return {
          kind: "creative-tags",
          dimensions: tags,
          subtitle: entry.subtitle,
        };
      }
      default: {
        return entry satisfies never;
      }
    }
  };
  return {
    featured: groupBy.featured.map(toFeatured),
    more: groupBy.more.map(toTarget),
  };
};

const attributeTargetsOf = (layout: GroupByLayout) =>
  [...layout.featured, ...layout.more].flatMap((target) =>
    target.kind === "attribute" ? [target] : [],
  );

const groupOwnedSegmentsOf = (
  layout: GroupByLayout,
): readonly Segment["name"][] =>
  attributeTargetsOf(layout).flatMap((target) => target.segments);

export const groupSegmentsOf = ({
  layout,
  member,
}: {
  layout: GroupByLayout;
  member: GroupDimension["name"];
}): readonly Segment["name"][] =>
  attributeTargetsOf(layout).flatMap((target) =>
    target.dimension.name === member ? target.segments : [],
  );

export const groupDimensionsOf = (
  layout: GroupByLayout,
): readonly GroupDimension[] =>
  [...layout.featured, ...layout.more].flatMap((target) => {
    switch (target.kind) {
      case "attribute": {
        return [target.dimension];
      }
      case "creative-tags": {
        return target.dimensions;
      }
      case "none":
      case "entities": {
        return [];
      }
      default: {
        return target satisfies never;
      }
    }
  });

const toViewBase = ({ cube, wire }: { cube: CubeView; wire: CubeWire }) => {
  const members = membersOf(cube);
  const valueLabels = toValueLabels(cube);
  return {
    folders: cube.folders.map(
      (folder): Folder => ({
        name: folder.name,
        members: folder.members.flatMap((name) => {
          const member = members.get(name);
          return member === undefined ? [] : toFilterMember(member);
        }),
      }),
    ),
    metrics: toMetricColumns(cube),
    valueLabels,
    labels: new Map(
      [...members.values()].map((member): [string, string] => [
        member.name,
        member.shortTitle,
      ]),
    ),
    aliases: new Map(
      [...members.values()].flatMap((member): [string, string][] =>
        member.aliasMember === undefined
          ? []
          : [[member.name, member.aliasMember]],
      ),
    ),
    channelsOf: new Map(
      [...members.values()].map((member): [string, readonly string[]] => [
        member.name,
        member.meta.channels,
      ]),
    ),
    segments: cube.segments.map(toSegment),
    controls: cube.meta.controls,
    filter: runnableFilterSchema(cube),
    wire,
  };
};

const toInsightsSync = ({
  id,
  channel,
}: {
  id: string;
  channel: string;
}): InsightsSync => {
  const registered = getInsightsChannel(channel);
  // Unreachable: cubeMetaSchema refuses a sync whose channel is not registered.
  if (registered === undefined) {
    throw new Error(`${channel} is not an insights channel`);
  }
  return { id, channel: registered };
};

export const toInsightsMeta = (
  meta: CubeMeta,
): InsightsMeta | "no-connected-channels" => {
  const { cubes } = meta;
  switch (cubes.length) {
    case 0: {
      return "no-connected-channels";
    }
    case 3: {
      const [ad, adSet, campaign] = cubes;
      const wire: CubeWire = {
        query: cubeQuerySchema(meta),
        response: (query) => cubeLoadResponseSchema({ query, meta }),
        blendResponse: (queries) => cubeBlendResponseSchema({ queries, meta }),
      };
      const [first, ...rest] = ad.meta.syncs;
      const { groupBy, ...adLayouts } = ad.meta.layouts;
      const adBase = toViewBase({ cube: ad, wire });
      const groupByLayout = toGroupByLayout({
        groupBy,
        dimensions: ad.dimensions,
      });
      const groupOwned = groupOwnedSegmentsOf(groupByLayout);
      return {
        views: {
          ad: {
            ...adBase,
            segments: adBase.segments.filter(
              ({ name }) => !groupOwned.includes(name),
            ),
            name: ad.name,
            groupBy: groupByLayout,
            layouts: adLayouts,
            entities: {
              campaign: ad.meta.entities.campaign,
              adSet: ad.meta.entities.adSet,
              entity: ad.meta.entities.ad,
            },
            byGrain: toEntitiesByGrain(ad.meta.entities),
          },
          ad_set: {
            ...toViewBase({ cube: adSet, wire }),
            name: adSet.name,
            layouts: adSet.meta.layouts,
            entities: {
              campaign: adSet.meta.entities.campaign,
              entity: adSet.meta.entities.adSet,
            },
            byGrain: toEntitiesByGrain(adSet.meta.entities),
          },
          campaign: {
            ...toViewBase({ cube: campaign, wire }),
            name: campaign.name,
            layouts: campaign.meta.layouts,
            entities: { entity: campaign.meta.entities.campaign },
            byGrain: toEntitiesByGrain(campaign.meta.entities),
          },
        },
        syncs: [toInsightsSync(first), ...rest.map(toInsightsSync)],
        defaultEvents: new Map(Object.entries(ad.meta.defaultEvents)),
      };
    }
    default: {
      return cubes satisfies never;
    }
  }
};

export const columnsFor = ({
  view,
  keys,
}: {
  view: ViewMeta;
  keys: readonly MetricKey[];
}): readonly MetricColumn[] => {
  return keys.flatMap((key) =>
    view.metrics.filter((column) => column.key === key),
  );
};

export function availableColumns({
  view,
  scope,
}: {
  view: ViewMeta;
  scope: readonly ChannelScope[];
}): readonly MetricColumn[] {
  const picked = new Set(scope.map(({ channel }) => channel.sourceType));
  return view.metrics.filter((column) =>
    column.channels.some((channel) => picked.has(channel)),
  );
}

export const filterFolders = ({
  view,
}: {
  view: ViewMeta;
}): readonly Folder[] =>
  [...view.folders].filter((folder) => folder.members.length > 0);

const NEUTRAL_TONE: BadgeVariant = "subtle";

const STATUS_TONES: ReadonlyMap<string, BadgeVariant> = new Map([
  ["active", "success"],
  ["paused", "warning"],
]);

export const statusBadge = ({
  view,
  value,
}: {
  view: ViewMeta;
  value: string;
}): CellBadge => ({
  label: view.valueLabels.get(view.layouts.table.status)?.get(value) ?? value,
  variant: STATUS_TONES.get(value) ?? NEUTRAL_TONE,
});

const FATIGUE_TONES: ReadonlyMap<string, BadgeVariant> = new Map(
  Object.entries(fatigueBadgeVariant),
);

export const fatigueBadge = ({
  view,
  value,
}: {
  view: AdViewMeta;
  value: string;
}): CellBadge => ({
  label: view.valueLabels.get(view.layouts.fatigue.status)?.get(value) ?? value,
  variant: FATIGUE_TONES.get(value) ?? NEUTRAL_TONE,
});

const groupByTitleOf = ({ dimension, view }: AttributeGroupBy): string =>
  view.groupBy.featured.flatMap((target) =>
    target.kind === "attribute" && target.dimension.name === dimension.name
      ? [target.label]
      : [],
  )[0] ?? dimension.shortTitle;

export function groupByLabel({
  groupBy,
  scope,
}: {
  groupBy: GroupBy;
  scope: NonEmptyArray<ChannelScope>;
}): string {
  switch (groupBy.kind) {
    case "ads": {
      return "None";
    }
    case "entities": {
      return toEntityLabel({ grain: groupBy.grain, scope });
    }
    case "attribute": {
      return groupByTitleOf(groupBy);
    }
    default: {
      return groupBy satisfies never;
    }
  }
}

const toChoices = ({
  target,
  views,
  toOption,
}: {
  target: GroupTarget;
  views: InsightsMeta["views"];
  toOption: <Picked extends GroupBy>(
    groupBy: Picked,
  ) => { label: string; groupBy: Picked };
}): GroupByChoice[] => {
  const option = (groupBy: GroupBy): GroupByChoice[] => [
    { kind: "option", option: toOption(groupBy) },
  ];
  const attributeOf = (dimension: GroupDimension): AttributeGroupBy => ({
    kind: "attribute",
    dimension,
    view: views.ad,
  });
  switch (target.kind) {
    case "attribute": {
      return option(attributeOf(target.dimension));
    }
    case "none": {
      return option(groupByOf({ views, key: { kind: "none" } }));
    }
    case "entities": {
      return option(
        groupByOf({ views, key: { kind: "entities", grain: target.grain } }),
      );
    }
    case "creative-tags": {
      const [first, ...rest] = target.dimensions.map((dimension) =>
        toOption(attributeOf(dimension)),
      );
      return first === undefined
        ? []
        : [{ kind: "creative-tags", options: [first, ...rest] }];
    }
    default: {
      return target satisfies never;
    }
  }
};

export function groupByMenu({
  views,
  scope,
}: {
  views: InsightsMeta["views"];
  scope: NonEmptyArray<ChannelScope>;
}): GroupByMenu {
  const toOption = <Picked extends GroupBy>(groupBy: Picked) => ({
    label: groupByLabel({ groupBy, scope }),
    groupBy,
  });
  const choicesOf = (target: GroupTarget) =>
    toChoices({ target, views, toOption });
  const { featured, more } = views.ad.groupBy;
  return {
    featured: featured.flatMap((target) =>
      choicesOf(target).map((choice) => ({
        ...choice,
        subtitle: target.subtitle,
      })),
    ),
    more: more.flatMap(choicesOf),
  };
}

export function groupByKeyOf(groupBy: GroupBy): GroupByKey {
  switch (groupBy.kind) {
    case "ads": {
      return { kind: "none" };
    }
    case "entities": {
      return { kind: "entities", grain: groupBy.grain };
    }
    case "attribute": {
      return { kind: "attribute", member: groupBy.dimension.name };
    }
    default: {
      return groupBy satisfies never;
    }
  }
}

export const toGroupById = (key: GroupByKey): string => {
  switch (key.kind) {
    case "none": {
      return "none";
    }
    case "entities": {
      return key.grain;
    }
    case "attribute": {
      return key.member;
    }
    default: {
      return key satisfies never;
    }
  }
};

export const groupByOf = ({
  views,
  key,
}: {
  views: InsightsMeta["views"];
  key: GroupByKey;
}): GroupBy => {
  switch (key.kind) {
    case "none": {
      return { kind: "ads", view: views.ad };
    }
    case "entities": {
      switch (key.grain) {
        case "ad_set": {
          return { kind: "entities", grain: "ad_set", view: views.ad_set };
        }
        case "campaign": {
          return { kind: "entities", grain: "campaign", view: views.campaign };
        }
        default: {
          return key.grain satisfies never;
        }
      }
    }
    case "attribute": {
      const dimension = groupDimensionsOf(views.ad.groupBy).find(
        (candidate) => candidate.name === key.member,
      );
      if (dimension === undefined) {
        throw new Error(`${key.member} is not a group-by of ${views.ad.name}`);
      }
      return { kind: "attribute", dimension, view: views.ad };
    }
    default: {
      return key satisfies never;
    }
  }
};

const ENTITY_GRAINS = {
  campaign: "campaign",
  adSet: "ad_set",
  ad: "ad",
} as const;

// Meta names each view's entities by grain, so a grain finds its id and name member without knowing the view.
export const entityOf = ({
  view,
  grain,
}: {
  view: ViewMeta;
  grain: Grain;
}): Found<AdViewMeta["entities"]["entity"]> => {
  const entity = view.byGrain.get(grain);
  return entity === undefined
    ? { kind: "not-found" }
    : { kind: "found", value: entity };
};

const toEntitiesByGrain = (
  entities: Readonly<Record<string, { id: string; name: string }>>,
): ReadonlyMap<Grain, { id: string; name: string }> =>
  new Map(
    Object.entries(ENTITY_GRAINS).flatMap(([key, grain]) => {
      const entity = entities[key];
      return entity === undefined ? [] : [[grain, entity] as const];
    }),
  );
